/**
 * Tela /administracao/integracoes-cad — gerencia configuracoes de adapters CAD
 * que requerem credenciais (ADR-0017, Fase 4 Bloco E).
 *
 * Fluxo:
 *   - Lista as configs cadastradas (uma por unidade x adapter)
 *   - Botao "Cadastrar credenciais" abre SlideOverPanel com form
 *   - Cada linha tem acoes: Testar conexao, Editar credenciais, Excluir
 *
 * Permissoes:
 *   - `integracao_cad:view` para ler
 *   - `integracao_cad:configurar` (+MFA recente) para criar/editar/excluir/testar
 *
 * 3 zonas:
 *   Zona 1: EntityHeader (titulo + acao primaria "Cadastrar credenciais")
 *   Zona 2: KpiStrip (total configs, testes recentes, ativos)
 *   Zona 3: Tabela master + SlideOverPanel de cadastro/edicao
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiKey, FiPlus, FiTrash2, FiZap } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import EntityHeader from "../../../components/EntityHeader";
import KpiStrip from "../../../components/KpiStrip";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import {
  atualizarConfigCad,
  criarConfigCad,
  excluirConfigCad,
  listarAdaptersCad,
  listarConfigsCad,
  testarConfigCad,
} from "../../../services/cad";
import { listarUnidades } from "../../../services/unidades";
import { PageContainer } from "../../../styles/shared";

import {
  Card,
  EmptyState,
  ErrorBox,
  Field,
  HelpText,
  InfoBox,
  Input,
  Pill,
  Row,
  SectionTitle,
  Select,
  Table,
  TableWrapper,
} from "./styles";

function fmtData(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR");
  } catch {
    return "—";
  }
}

function toneAtivo(ativo) {
  return ativo ? "success" : "neutral";
}

function toneToken(token_valido) {
  return token_valido ? "success" : "warning";
}

export default function IntegracoesCAD() {
  const { request, hasPermission } = useAuth();
  const toast = useToast();
  // Quem só tem `integracao_cad:view` lê a lista; criar/editar/excluir/testar
  // exigem `integracao_cad:configurar` no backend (hasPermission já cobre Master).
  const podeConfigurar = hasPermission("integracao_cad:configurar");

  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [adapters, setAdapters] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [configs, setConfigs] = useState([]);

  // form state (cadastro/edicao)
  const [formAberto, setFormAberto] = useState(false);
  const [editando, setEditando] = useState(null); // null = create
  const [formData, setFormData] = useState({
    business_unit: "",
    adapter_id: "",
    usuario: "",
    senha: "",
    ativo: true,
  });
  const [formErro, setFormErro] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const [adaptersPayload, unidadesPayload, configsPayload] = await Promise.all([
        listarAdaptersCad(request),
        listarUnidades(request),
        listarConfigsCad(request),
      ]);
      // Filtrar so adapters que requerem configuracao
      const apenasComConfig = (adaptersPayload?.adapters || []).filter(
        (a) => a.requer_configuracao,
      );
      setAdapters(apenasComConfig);
      const items = Array.isArray(unidadesPayload)
        ? unidadesPayload
        : unidadesPayload?.results || [];
      setUnidades(items);
      const cfgs = Array.isArray(configsPayload)
        ? configsPayload
        : configsPayload?.results || [];
      setConfigs(cfgs);
    } catch (err) {
      setErro(err?.message || "Erro ao carregar configurações.");
    } finally {
      setCarregando(false);
    }
  }, [request]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const kpis = useMemo(
    () => [
      { label: "Configs cadastradas", value: configs.length },
      {
        label: "Configs ativas",
        value: configs.filter((c) => c.ativo).length,
        tone: "success",
      },
      {
        label: "Adapters disponíveis",
        value: adapters.length,
        tone: "info",
      },
    ],
    [configs, adapters],
  );

  const abrirNovo = () => {
    setEditando(null);
    setFormData({
      business_unit: unidades[0]?.id || "",
      adapter_id: adapters[0]?.adapter_id || "",
      usuario: "",
      senha: "",
      ativo: true,
    });
    setFormErro(null);
    setFormAberto(true);
  };

  const abrirEdicao = (config) => {
    setEditando(config);
    setFormData({
      business_unit: config.business_unit,
      adapter_id: config.adapter_id,
      usuario: "",
      senha: "",
      ativo: config.ativo,
    });
    setFormErro(null);
    setFormAberto(true);
  };

  const fechar = () => {
    setFormAberto(false);
    setEditando(null);
    setFormErro(null);
  };

  const salvar = async () => {
    setFormErro(null);
    if (editando) {
      // Edicao: usuario+senha sao opcionais (se vazios, mantem)
      const payload = { ativo: formData.ativo };
      if (formData.usuario || formData.senha) {
        if (!formData.usuario || !formData.senha) {
          setFormErro("Para trocar credenciais, preencha usuário E senha.");
          return;
        }
        payload.usuario = formData.usuario;
        payload.senha = formData.senha;
      }
      try {
        await atualizarConfigCad(request, editando.id, payload);
        toast.success("Configuração atualizada.");
        fechar();
        await carregar();
      } catch (err) {
        setFormErro(err?.message || "Erro ao atualizar.");
        throw err;
      }
    } else {
      if (!formData.business_unit || !formData.adapter_id) {
        setFormErro("Selecione unidade e adapter.");
        return;
      }
      if (!formData.usuario || !formData.senha) {
        setFormErro("Usuário e senha são obrigatórios.");
        return;
      }
      try {
        await criarConfigCad(request, {
          business_unit: formData.business_unit,
          adapter_id: formData.adapter_id,
          usuario: formData.usuario,
          senha: formData.senha,
          ativo: formData.ativo,
        });
        toast.success("Credenciais cadastradas.");
        fechar();
        await carregar();
      } catch (err) {
        setFormErro(err?.message || "Erro ao cadastrar.");
        throw err;
      }
    }
  };

  const testar = async (config) => {
    const out = await testarConfigCad(request, config.id);
    if (out?.ok) {
      toast.success("Conexão validada com sucesso.");
    } else {
      toast.error(out?.mensagem || "Falha na validação.");
    }
    await carregar();
  };

  const excluir = async (config) => {
    const adapter = adapters.find((a) => a.adapter_id === config.adapter_id);
    const ok = window.confirm(
      `Excluir credenciais de ${adapter?.descricao || config.adapter_id} `
      + `para ${config.business_unit_codigo}? Esta ação não pode ser desfeita.`,
    );
    if (!ok) return;
    await excluirConfigCad(request, config.id);
    toast.success("Credenciais excluídas.");
    await carregar();
  };

  return (
    <PageContainer>
      <EntityHeader
        title="Integrações CAD"
        subtitle="Credenciais de sistemas CAD/3D externos (DinaBox, etc)"
        primaryAction={
          podeConfigurar && adapters.length > 0
            ? { label: "Cadastrar credenciais", icon: <FiPlus />, onClick: abrirNovo }
            : undefined
        }
      />
      <KpiStrip items={kpis} />

      {erro && <ErrorBox>{erro}</ErrorBox>}

      {adapters.length === 0 && !carregando && (
        <InfoBox>
          Nenhum adapter CAD que exige credenciais esta registrado no backend
          neste momento. Verifique o registry em <code>core.cad.registry</code>.
        </InfoBox>
      )}

      <Card>
        <SectionTitle>Configurações cadastradas</SectionTitle>
        {carregando ? (
          <EmptyState>Carregando...</EmptyState>
        ) : configs.length === 0 ? (
          <EmptyState>
            Nenhuma credencial cadastrada ainda.
            {podeConfigurar
              ? ' Clique em "Cadastrar credenciais" para integrar com DinaBox via API.'
              : " Fale com quem configura as integrações para cadastrar."}
          </EmptyState>
        ) : (
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th style={{ width: "16%" }}>Unidade</th>
                  <th style={{ width: "26%" }}>Adapter</th>
                  <th style={{ width: "12%" }}>Status</th>
                  <th style={{ width: "12%" }}>Token</th>
                  <th style={{ width: "16%" }}>Última validação</th>
                  <th style={{ width: "18%" }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {configs.map((c) => (
                  <tr key={c.id}>
                    <td title={c.business_unit_nome}>
                      <strong>{c.business_unit_codigo}</strong>
                    </td>
                    <td title={c.adapter_descricao}>{c.adapter_descricao}</td>
                    <td>
                      <Pill $tone={toneAtivo(c.ativo)}>
                        {c.ativo ? "Ativa" : "Inativa"}
                      </Pill>
                    </td>
                    <td>
                      <Pill $tone={toneToken(c.token_valido)}>
                        {c.token_valido ? "Válido" : "Renovar"}
                      </Pill>
                    </td>
                    <td title={c.ultimo_erro || ""}>
                      {fmtData(c.ultima_validacao_em)}
                    </td>
                    <td>
                      {podeConfigurar ? (
                      <Row style={{ gap: 4 }}>
                        <AsyncButton
                          variant="secondary"
                          onClick={() => testar(c)}
                          silentError
                          title="Testar conexão"
                        >
                          <FiZap /> Testar
                        </AsyncButton>
                        <AsyncButton
                          variant="secondary"
                          onClick={async () => abrirEdicao(c)}
                          silentSuccess
                          silentError
                          title="Editar"
                        >
                          <FiKey />
                        </AsyncButton>
                        <AsyncButton
                          variant="danger"
                          onClick={() => excluir(c)}
                          silentSuccess
                          silentError
                          title="Excluir"
                        >
                          <FiTrash2 />
                        </AsyncButton>
                      </Row>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
        {configs.some((c) => c.ultimo_erro) && (
          <HelpText>
            Configs com erro recente: passe o mouse sobre "Última validação"
            para ver a mensagem original. Clique em "Testar" para revalidar.
          </HelpText>
        )}
      </Card>

      <SlideOverPanel
        open={formAberto}
        onClose={fechar}
        title={editando ? "Editar credenciais" : "Cadastrar credenciais CAD"}
        subtitle={
          editando
            ? `${editando.business_unit_codigo} x ${editando.adapter_descricao}`
            : "Nova integração por unidade x adapter"
        }
        footer={
          <Row style={{ justifyContent: "flex-end" }}>
            <AsyncButton variant="secondary" onClick={async () => fechar()} silentSuccess silentError>
              Cancelar
            </AsyncButton>
            <AsyncButton
              variant="primary"
              onClick={salvar}
              silentSuccess
              silentError
              successMessage=""
            >
              {editando ? "Salvar mudanças" : "Cadastrar"}
            </AsyncButton>
          </Row>
        }
      >
        {formErro && <ErrorBox style={{ marginBottom: 12 }}>{formErro}</ErrorBox>}

        <Field>
          <span>Unidade</span>
          <Select
            value={formData.business_unit}
            onChange={(e) => setFormData({ ...formData, business_unit: e.target.value })}
            disabled={Boolean(editando)}
          >
            <option value="">— escolha uma unidade —</option>
            {unidades.map((u) => (
              <option key={u.id} value={u.id}>
                {u.codigo} — {u.nome}
              </option>
            ))}
          </Select>
        </Field>

        <Field>
          <span>Adapter CAD</span>
          <Select
            value={formData.adapter_id}
            onChange={(e) => setFormData({ ...formData, adapter_id: e.target.value })}
            disabled={Boolean(editando)}
          >
            <option value="">— escolha um adapter —</option>
            {adapters.map((a) => (
              <option key={a.adapter_id} value={a.adapter_id}>
                {a.descricao}
              </option>
            ))}
          </Select>
        </Field>

        <Field>
          <span>
            Usuário {editando && "(opcional — deixe vazio para não alterar)"}
          </span>
          <Input
            type="text"
            value={formData.usuario}
            onChange={(e) => setFormData({ ...formData, usuario: e.target.value })}
            placeholder={editando ? "(manter atual)" : "usuário DinaBox"}
            autoComplete="off"
          />
        </Field>

        <Field>
          <span>
            Senha {editando && "(opcional — deixe vazio para não alterar)"}
          </span>
          <Input
            type="password"
            value={formData.senha}
            onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
            placeholder={editando ? "(manter atual)" : "senha DinaBox"}
            autoComplete="new-password"
          />
        </Field>

        <Field>
          <span>Status</span>
          <Select
            value={formData.ativo ? "1" : "0"}
            onChange={(e) => setFormData({ ...formData, ativo: e.target.value === "1" })}
          >
            <option value="1">Ativa</option>
            <option value="0">Inativa (mantida mas não usada)</option>
          </Select>
        </Field>

        <HelpText>
          As credenciais são cifradas em repouso (Fernet) e nunca retornam ao frontend
          apos salvar. Para trocar, basta preencher os campos novamente. Senhas em
          branco numa edição = mantém as atuais.
        </HelpText>
      </SlideOverPanel>
    </PageContainer>
  );
}
